import { Fragment, ReactNode } from 'react';
import classNames from 'classnames';

const URL_PATTERN = /(https?:\/\/[^\s]+)/g;
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

const isUrl = (value: string): boolean => /^https?:\/\//.test(value);

// Splitting on a capturing pattern keeps each URL as its own segment, so the
// bare links in a brief render as clickable anchors while prose stays plain.
const renderWithLinks = (text: string): ReactNode[] =>
    text.split(URL_PATTERN).map((segment, index) => {
        if (!isUrl(segment)) {
            return <Fragment key={index}>{segment}</Fragment>;
        }
        // Sentence punctuation trailing a URL shouldn't be part of the href.
        const trailingMatch = segment.match(TRAILING_PUNCTUATION);
        const trailing = trailingMatch ? trailingMatch[0] : '';
        const href = trailing ? segment.slice(0, -trailing.length) : segment;
        return (
            <Fragment key={index}>
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-words text-wm-accent underline underline-offset-2"
                >
                    {href}
                </a>
                {trailing}
            </Fragment>
        );
    });

type BriefTextProps = {
    brief: string;
    className?: string;
};

/**
 * Plain-text campaign brief content with its authored whitespace preserved and
 * bare HTTP(S) URLs made clickable. React escapes the remaining text, so brief
 * content is never interpreted as HTML.
 */
export const BriefText = ({ brief, className }: BriefTextProps) => (
    <p className={classNames('whitespace-pre-wrap break-words', className)}>{renderWithLinks(brief)}</p>
);
